import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';

export type DataMode = 'demo' | 'live';

export interface ScreenDataMode {
  screenId: string;
  screenName: string;
  mode: DataMode;
  category?: string;
}

interface DataSourceContextType {
  // Global mode (legacy support)
  mode: DataMode;
  setMode: (mode: DataMode) => void;
  isDemo: boolean;
  isLive: boolean;
  
  // Per-screen mode configuration
  screenModes: Record<string, DataMode>;
  getScreenMode: (screenId: string) => DataMode;
  setScreenMode: (screenId: string, mode: DataMode) => void;
  getAllScreenModes: () => ScreenDataMode[];
  resetAllScreenModes: (mode: DataMode) => void;
}

const DataSourceContext = createContext<DataSourceContextType | undefined>(undefined);

const STORAGE_KEY = 'timetrack-data-source';
const SCREEN_MODES_KEY = 'timetrack-screen-modes';
const VERSION_KEY = 'timetrack-version';
const CURRENT_VERSION = 'TimeTrackFinal-v1';

// Default screen configurations
const DEFAULT_SCREENS: ScreenDataMode[] = [
  // Main Navigation
  { screenId: 'dashboard', screenName: 'Dashboard', mode: 'demo', category: 'Main' },
  { screenId: 'timesheet', screenName: 'My Timesheet', mode: 'demo', category: 'Main' },
  { screenId: 'approvals', screenName: 'Approvals', mode: 'demo', category: 'Main' },
  
  // Reports & Analytics
  { screenId: 'reports-timesheet', screenName: 'Timesheet Reports', mode: 'demo', category: 'Reports' },
  { screenId: 'reports-utilization', screenName: 'Utilization Reports', mode: 'demo', category: 'Reports' },
  { screenId: 'reports-billable', screenName: 'Billable Hours', mode: 'demo', category: 'Reports' },
  { screenId: 'reports-project', screenName: 'Project Reports', mode: 'demo', category: 'Reports' },
  { screenId: 'reports-custom', screenName: 'Custom Reports', mode: 'demo', category: 'Reports' },
  { screenId: 'analytics', screenName: 'Analytics', mode: 'demo', category: 'Reports' },
  
  // Team Management
  { screenId: 'team-members', screenName: 'Team Members', mode: 'demo', category: 'Team' },
  { screenId: 'team-schedule', screenName: 'Team Schedule', mode: 'demo', category: 'Team' },
  { screenId: 'team-capacity', screenName: 'Capacity Planning', mode: 'demo', category: 'Team' },
  
  // Projects
  { screenId: 'projects-active', screenName: 'Active Projects', mode: 'demo', category: 'Projects' },
  { screenId: 'projects-archive', screenName: 'Archived Projects', mode: 'demo', category: 'Projects' },
  { screenId: 'projects-budget', screenName: 'Budget Tracking', mode: 'demo', category: 'Projects' },
  
  // Integrations
  { screenId: 'integrations', screenName: 'Integrations', mode: 'demo', category: 'Integrations' },
  
  // Data Import/Export
  { screenId: 'import', screenName: 'Import Data', mode: 'demo', category: 'Data' },
  { screenId: 'export', screenName: 'Export Data', mode: 'demo', category: 'Data' },
  
  // Settings (last section)
  { screenId: 'settings-general', screenName: 'General', mode: 'demo', category: 'Settings' },
  { screenId: 'roles-rates', screenName: 'Roles & Rates', mode: 'demo', category: 'Settings' },
  { screenId: 'settings-approval', screenName: 'Approval Rules', mode: 'demo', category: 'Settings' },
];

export const DataSourceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Global mode (legacy)
  const [mode, setModeState] = useState<DataMode>(() => {
    const savedVersion = localStorage.getItem(VERSION_KEY);
    
    if (savedVersion !== CURRENT_VERSION) {
      localStorage.setItem(VERSION_KEY, CURRENT_VERSION);
      localStorage.setItem(STORAGE_KEY, 'demo');
      return 'demo';
    }
    
    const saved = localStorage.getItem(STORAGE_KEY);
    return (saved as DataMode) || 'demo';
  });

  // Per-screen modes
  const [screenModes, setScreenModes] = useState<Record<string, DataMode>>(() => {
    const saved = localStorage.getItem(SCREEN_MODES_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        // If parsing fails, initialize with defaults
      }
    }
    
    // Initialize with default modes
    const defaultModes: Record<string, DataMode> = {};
    DEFAULT_SCREENS.forEach(screen => {
      defaultModes[screen.screenId] = screen.mode;
    });
    return defaultModes;
  });

  // Persist global mode
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  // Persist screen modes
  useEffect(() => {
    localStorage.setItem(SCREEN_MODES_KEY, JSON.stringify(screenModes));
  }, [screenModes]);

  const setMode = (newMode: DataMode) => {
    setModeState(newMode);
  };

  const getScreenMode = (screenId: string): DataMode => {
    return screenModes[screenId] || 'demo';
  };

  const setScreenMode = (screenId: string, newMode: DataMode) => {
    setScreenModes(prev => ({
      ...prev,
      [screenId]: newMode,
    }));
  };

  const getAllScreenModes = (): ScreenDataMode[] => {
    return DEFAULT_SCREENS.map(screen => ({
      ...screen,
      mode: screenModes[screen.screenId] || screen.mode,
    }));
  };

  const resetAllScreenModes = (newMode: DataMode) => {
    const newModes: Record<string, DataMode> = {};
    DEFAULT_SCREENS.forEach(screen => {
      newModes[screen.screenId] = newMode;
    });
    setScreenModes(newModes);
  };

  const value: DataSourceContextType = {
    mode,
    setMode,
    isDemo: mode === 'demo',
    isLive: mode === 'live',
    screenModes,
    getScreenMode,
    setScreenMode,
    getAllScreenModes,
    resetAllScreenModes,
  };

  return (
    <DataSourceContext.Provider value={value}>
      {children}
    </DataSourceContext.Provider>
  );
};

export const useDataSource = () => {
  const context = useContext(DataSourceContext);
  if (context === undefined) {
    throw new Error('useDataSource must be used within a DataSourceProvider');
  }
  return context;
};
